import React from 'react';

// ─── Toggle switch ────────────────────────────────────────────────────────────

export const ToggleSwitch = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => {
      if (disabled || typeof onChange !== 'function') return;
      onChange();
    }}
    style={{
      width: '32px',
      height: '18px',
      borderRadius: '9px',
      border: 'none',
      backgroundColor: checked ? '#489291' : 'rgba(255,255,255,0.18)',
      position: 'relative',
      cursor: disabled ? 'not-allowed' : 'pointer',
      flexShrink: 0,
      transition: 'background-color 0.18s ease',
      padding: 0,
      opacity: disabled ? 0.45 : 1,
    }}
  >
    <span
      style={{
        position: 'absolute',
        top: '2px',
        left: checked ? '16px' : '2px',
        width: '14px',
        height: '14px',
        borderRadius: '50%',
        backgroundColor: '#ffffff',
        transition: 'left 0.18s ease',
        boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
      }}
    />
  </button>
);

// ─── Fila genérica de capa de referencia ──────────────────────────────────────

/**
 * Contenedor visual reutilizable para cada capa del panel.
 *
 * Props:
 *  - icon       Componente de icono MUI (p. ej. WaterDropOutlined).
 *  - title      Nombre principal de la capa.
 *  - sublabel   Descripción corta (fuente, cobertura…).
 *  - enabled    Si la capa está activa.
 *  - onToggle   Callback al hacer click en el toggle.
 *  - iconColor  Color del glifo (p. ej. kind de lugares). Si no, teal activo / blanco inactivo.
 *  - children   Detalle expandible visible solo cuando `enabled` es true.
 */
const ReferenceLayerRow = ({
  icon: Icon,
  title,
  sublabel,
  enabled,
  onToggle,
  children,
  disabled = false,
  disabledSublabel = 'Iniciá sesión para activar esta capa',
  mapZoom = null,
  minZoomToView = null,
  zoomHintSublabel = 'Acercá más el zoom',
  iconColor = null,
}) => {
  const isActive = enabled && !disabled;
  const parsedZoom = Number.parseFloat(mapZoom);
  const needsMoreZoom = (
    isActive
    && minZoomToView != null
    && Number.isFinite(parsedZoom)
    && parsedZoom < minZoomToView
  );

  const displaySublabel = disabled
    ? disabledSublabel
    : needsMoreZoom
      ? zoomHintSublabel
      : sublabel;
  const sublabelColor = disabled
    ? 'rgba(255,255,255,0.28)'
    : needsMoreZoom
      ? 'rgba(72, 146, 145, 0.92)'
      : 'rgba(255,255,255,0.35)';

  return (
  <div style={{
    borderRadius: '8px',
    border: isActive
      ? '1px solid rgba(72,146,145,0.4)'
      : '1px solid rgba(255,255,255,0.07)',
    backgroundColor: isActive
      ? 'rgba(72,146,145,0.08)'
      : 'rgba(255,255,255,0.02)',
    padding: '8px',
    transition: 'background-color 0.15s, border-color 0.15s',
    opacity: disabled ? 0.45 : 1,
    width: '100%',
    boxSizing: 'border-box',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isActive
          ? 'rgba(72,146,145,0.14)'
          : 'rgba(255,255,255,0.05)',
        border: isActive
          ? '1.5px solid rgba(72,146,145,0.5)'
          : '1.5px solid rgba(255,255,255,0.12)',
      }}>
        <Icon
          sx={{
            fontSize: '22px',
            color: iconColor || (isActive ? '#489291' : 'rgba(255,255,255,0.55)'),
          }}
        />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '13px',
          fontWeight: isActive ? '600' : '400',
          color: isActive ? '#ffffff' : 'rgba(255,255,255,0.75)',
          lineHeight: 1.2,
          minWidth: 0,
        }}>
          {title}
        </div>
        <div style={{
          fontSize: '10px',
          color: sublabelColor,
          marginTop: '2px',
        }}>
          {displaySublabel}
        </div>
      </div>

      <ToggleSwitch checked={isActive} onChange={onToggle} disabled={disabled} />
    </div>

    {isActive && children}
  </div>
  );
};

export default ReferenceLayerRow;
